import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Info, Plus, ShoppingBag, X } from "lucide-react";
import { imgUri, useMenu } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useCart } from "../lib/cart";
import { chf } from "../lib/format";
import { Stepper } from "../components/Stepper";

const SIZE_GUIDE = { "32": "👤", "40": "👥", "50": "👥👥" };

export default function ProductPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const lineParam = params.get("line");
  const optionParam = params.get("option");
  const navigate = useNavigate();
  const { t, tx } = useI18n();
  const { data, isLoading } = useMenu();
  const cart = useCart();

  const product = data?.products.find((p) => p.id === id);
  const editing = lineParam ? cart.items.find((i) => i.line_id === lineParam) : undefined;
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate("/"));

  const [qty, setQty] = useState(1);
  const [sizeKey, setSizeKey] = useState(null);
  const [optKeys, setOptKeys] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [extras, setExtras] = useState([]);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!product) return;
    if (editing) {
      setQty(editing.quantity);
      setSizeKey(editing.size?.key ?? null);
      setOptKeys(editing.options.map((o) => o.key));
      setRemoved(editing.removed_ingredients);
      setExtras(editing.extras);
      setNote(editing.note);
      return;
    }
    setSizeKey(product.sizes[0]?.key ?? null);
    const defaults = product.options.filter((o) => o.default).map((o) => o.key);
    if (optionParam && product.options.some((o) => o.key === optionParam)) {
      const chosen = product.options.find((o) => o.key === optionParam);
      setOptKeys([...defaults.filter((k) => product.options.find((o) => o.key === k)?.group !== chosen.group), optionParam]);
      if (chosen.only_sizes.length) setSizeKey(chosen.only_sizes[0]);
    } else setOptKeys(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, editing?.line_id]);

  // Always open a product page at the very top — the menu's scroll position must not carry over.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const size = product?.sizes.find((s) => s.key === sizeKey) ?? product?.sizes[0] ?? null;
  const optionPrice = (o) => (size && o.price_by_size[size.key] !== undefined ? o.price_by_size[size.key] : o.price);
  const optionAllowed = (o) => !o.only_sizes.length || !size || o.only_sizes.includes(size.key);
  const extraPrice = (e) => (size && e.price_by_size && e.price_by_size[size.key] !== undefined ? e.price_by_size[size.key] : e.price);

  const groups = useMemo(() => {
    const g = {};
    product?.options.forEach((o) => (g[o.group] = [...(g[o.group] || []), o]));
    return g;
  }, [product]);

  const allowedExtras = useMemo(() => {
    if (!product || !data) return [];
    const allowed = new Set(product.allowed_extra_ids);
    return data.extras.filter((e) => e.available && (allowed.has(e.key) || allowed.has(e.id)));
  }, [product, data]);

  // Re-price selected supplements when the size changes.
  useEffect(() => {
    setExtras((prev) => prev.map((x) => {
      const e = allowedExtras.find((ae) => ae.id === x.extra_id);
      return e ? { ...x, unit_price: extraPrice(e) } : x;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeKey]);

  if (isLoading || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-hm-surface">
        <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-hm-line border-t-hm-brand" />
      </div>
    );
  }
  if (!product) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-hm-surface">
        <p className="font-sans text-hm-muted">{t("unavailable")}</p>
        <button type="button" onClick={() => navigate("/")} className="rounded-full border-[1.5px] border-hm-lineStrong px-6 py-3 font-sans font-bold text-hm-ink">
          {t("back")}
        </button>
      </div>
    );
  }

  const selectedOptions = (product.options || []).filter((o) => optKeys.includes(o.key) && !o.default && optionAllowed(o));
  const basePrice = size ? size.price : product.price ?? 0;
  const optionsSum = selectedOptions.reduce((s, o) => s + optionPrice(o), 0);
  const extrasSum = extras.reduce((s, e) => s + e.unit_price * e.quantity, 0);
  const unit = basePrice + optionsSum;
  const total = (unit + extrasSum) * qty;
  const isCustom = product.ingredients.length <= 3 && allowedExtras.length > 10 && product.customizable;

  const selectSize = (k) => {
    setSizeKey(k);
    setOptKeys((prev) => {
      const next = prev.filter((key) => {
        const o = product.options.find((x) => x.key === key);
        return !o || !o.only_sizes.length || o.only_sizes.includes(k);
      });
      Object.values(groups).forEach((list) => {
        if (list.length && !list.some((o) => next.includes(o.key))) {
          const d = list.find((o) => o.default);
          if (d) next.push(d.key);
        }
      });
      return next;
    });
  };
  const chooseOption = (o, list) => {
    if (!optionAllowed(o)) return;
    const radio = list.some((x) => x.default);
    setOptKeys((prev) => {
      if (radio) return [...prev.filter((k) => !list.some((x) => x.key === k)), o.key];
      return prev.includes(o.key) ? prev.filter((k) => k !== o.key) : [...prev, o.key];
    });
  };
  const toggleIngredient = (ing) => {
    setRemoved((prev) => (prev.some((r) => r.id === ing.id) ? prev.filter((r) => r.id !== ing.id) : [...prev, ing]));
  };
  const setExtraQty = (e, q) => {
    setExtras((prev) => {
      const rest = prev.filter((x) => x.extra_id !== e.id);
      return q <= 0 ? rest : [...rest, { extra_id: e.id, key: e.key, name: e.name, unit_price: extraPrice(e), quantity: q }];
    });
  };

  const submit = () => {
    const payload = {
      product_id: product.id,
      name: product.name,
      image_url: product.image_url,
      unit_price: unit,
      quantity: qty,
      size,
      options: selectedOptions.map((o) => ({ key: o.key, name: o.name, price: optionPrice(o) })),
      removed_ingredients: removed,
      extras,
      note: note.trim(),
      is_alcohol: product.is_alcohol,
      alcohol_type: product.alcohol_type,
    };
    if (editing) cart.updateItem(editing.line_id, payload);
    else cart.addItem(payload);
    toast.success(`${tx(product.name)} – ${t("added")}`, { duration: 3000 });
    goBack();
  };

  return (
    <div className="min-h-screen bg-hm-surface pb-32">
      {/* Hero */}
      <div className="relative h-[340px] w-full bg-hm-tertiary sm:h-[400px]">
        <img src={imgUri(product.image_url)} alt={tx(product.name)} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <button
          type="button"
          data-testid="product-close-button"
          onClick={goBack}
          className="absolute left-4 top-4 flex h-[42px] w-[42px] items-center justify-center rounded-full bg-hm-surface text-hm-ink shadow-lg"
        >
          <X size={20} />
        </button>
        <div className="absolute bottom-11 left-0 right-0 mx-auto w-full max-w-[760px] px-6">
          <div className="mb-2 flex gap-2">
            {product.highlight === "moment" ? <Badge label={t("pizzaOfMonth")} tone="brand" /> : null}
            {product.is_alcohol ? <Badge label={`${product.alcohol_type === "spirits" ? 18 : 16}+`} tone="warning" /> : null}
            {!product.available ? <Badge label={t("soldOut")} tone="error" /> : null}
          </div>
          <h1 className="font-display text-4xl leading-tight text-white" data-testid="product-detail-name">{tx(product.name)}</h1>
          <p className="mt-1.5 font-sans text-lg font-bold text-white/90">
            {product.sizes.length > 1 ? `${t("from")} ` : ""}{chf(product.sizes[0]?.price ?? product.price)}
          </p>
        </div>
      </div>

      <div className="mx-auto -mt-7 w-full max-w-[760px] rounded-t-[28px] bg-hm-surface px-6 pt-7">
        <p className="font-sans text-base leading-6 text-hm-ink" data-testid="product-detail-description">{tx(product.description)}</p>

        <div className="mt-3.5 flex items-center gap-2 rounded-xl bg-hm-tertiary p-3">
          <Info size={14} className="text-hm-muted" />
          <span className="flex-1 font-sans text-[13px] text-hm-ink">{t("allergens")}: {tx(product.allergens) || t("notConfigured")}</span>
          {tx(product.origin) ? <span className="font-sans text-[13px] text-hm-ink" data-testid="product-origin">{t("origin")}: {tx(product.origin)}</span> : null}
        </div>

        {/* Sizes */}
        {product.sizes.length > 1 ? (
          <Section title={t("size")}>
            <div className="flex gap-2.5">
              {product.sizes.map((s) => {
                const on = size?.key === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    data-testid={`size-option-${s.key}`}
                    onClick={() => selectSize(s.key)}
                    className={`flex h-[76px] flex-1 flex-col items-center justify-center gap-0.5 rounded-2xl border-[1.5px] transition-colors ${
                      on ? "border-hm-inverse bg-hm-inverse text-white" : "border-hm-line bg-hm-card text-hm-ink"
                    }`}
                  >
                    <span className="font-display text-xl">{s.label}</span>
                    {SIZE_GUIDE[s.key] ? <span className="text-xs opacity-80" data-testid={`size-guide-${s.key}`}>{SIZE_GUIDE[s.key]}</span> : null}
                    <span className={`font-sans text-[13px] font-bold ${on ? "text-white/80" : "text-hm-muted"}`}>{chf(s.price)}</span>
                  </button>
                );
              })}
            </div>
          </Section>
        ) : null}

        {/* Dough & options */}
        {Object.entries(groups).map(([group, list]) => (
          <Section key={group} title={group === "dough" ? t("dough") : t("options")}>
            <div className="overflow-hidden rounded-2xl border border-hm-line bg-hm-card">
              {list.map((o, i) => {
                const on = optKeys.includes(o.key);
                const ok = optionAllowed(o);
                const p = optionPrice(o);
                return (
                  <button
                    key={o.key}
                    type="button"
                    data-testid={`option-${o.key}`}
                    disabled={!ok}
                    onClick={() => chooseOption(o, list)}
                    className={`flex min-h-[56px] w-full items-center gap-3 px-3.5 py-2.5 text-left ${i < list.length - 1 ? "border-b border-hm-line" : ""} ${!ok ? "opacity-45" : ""}`}
                  >
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${on ? "border-hm-brand bg-hm-brand" : "border-hm-lineStrong"}`}>
                      {on ? <Check size={14} className="text-white" /> : null}
                    </span>
                    <span className="flex-1">
                      <span className="block font-sans text-[15px] font-semibold text-hm-ink">{tx(o.name)}</span>
                      {o.only_sizes.length ? (
                        <span className="block font-sans text-[13px] text-hm-muted" data-testid={`option-${o.key}-sizes`}>
                          {t("onlyInSizes").replace("{sizes}", o.only_sizes.map((k) => `${k} cm`).join(", "))}
                        </span>
                      ) : null}
                    </span>
                    <span className="font-sans text-sm font-bold text-hm-ink">{p > 0 ? `+ ${chf(p)}` : ""}</span>
                  </button>
                );
              })}
            </div>
          </Section>
        ))}

        {/* Remove ingredients */}
        {product.customizable && product.ingredients.length > 0 && !isCustom ? (
          <Section title={t("removeIngredients")}>
            <p className="-mt-1 mb-3 font-sans text-[13px] text-hm-muted">{t("removeHint")}</p>
            <div className="flex flex-wrap gap-2">
              {product.ingredients.map((ing) => {
                const on = removed.some((r) => r.id === ing.id);
                return (
                  <button
                    key={ing.id}
                    type="button"
                    data-testid={`ingredient-chip-${ing.id}`}
                    onClick={() => toggleIngredient(ing)}
                    className={`h-9 rounded-full border px-3.5 font-sans text-sm font-semibold transition-colors ${
                      on ? "border-hm-error bg-hm-error/10 text-hm-error line-through" : "border-hm-line bg-hm-card text-hm-ink"
                    }`}
                  >
                    {tx(ing)}
                  </button>
                );
              })}
            </div>
          </Section>
        ) : null}

        {/* Extras */}
        {product.customizable && allowedExtras.length > 0 ? (
          <Section title={isCustom ? t("ingredients") : t("extras")}>
            <div className="overflow-hidden rounded-2xl border border-hm-line bg-hm-card">
              {allowedExtras.map((e, i) => {
                const cur = extras.find((x) => x.extra_id === e.id)?.quantity ?? 0;
                return (
                  <div key={e.id} className={`flex min-h-[56px] items-center gap-3 px-3.5 py-2.5 ${i < allowedExtras.length - 1 ? "border-b border-hm-line" : ""}`} data-testid={`extra-row-${e.key}`}>
                    <span className="flex-1">
                      <span className={`block font-sans text-[15px] font-semibold ${cur > 0 ? "text-hm-olive" : "text-hm-ink"}`}>{tx(e.name)}</span>
                      <span className="block font-sans text-[13px] text-hm-muted">+ {chf(extraPrice(e))}</span>
                    </span>
                    {cur === 0 ? (
                      <button
                        type="button"
                        data-testid={`extra-add-${e.key}`}
                        onClick={() => setExtraQty(e, 1)}
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-hm-tertiary text-hm-ink transition-colors hover:bg-hm-lineStrong"
                      >
                        <Plus size={18} />
                      </button>
                    ) : (
                      <Stepper value={cur} min={0} max={e.max_quantity} size={32} onChange={(v) => setExtraQty(e, v)} testId={`extra-stepper-${e.key}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </Section>
        ) : null}

        {/* Kitchen note */}
        {product.customizable ? (
          <Section title={t("kitchenNote")}>
            <textarea
              data-testid="product-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("kitchenNotePlaceholder")}
              rows={3}
              className="w-full rounded-xl border border-hm-line bg-hm-card px-3.5 py-3 font-sans text-base text-hm-ink outline-none focus:border-hm-brand"
            />
          </Section>
        ) : null}

        <div className="mt-6 flex items-center justify-between">
          <span className="font-display text-xl text-hm-ink">{t("quantity")}</span>
          <Stepper value={qty} onChange={setQty} size={40} testId="product-qty-stepper" />
        </div>
      </div>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-hm-line bg-hm-card px-5 pt-3.5 pb-4">
        <div className="mx-auto flex w-full max-w-[760px] items-center gap-4">
          <div className="flex-1">
            <p className="font-sans text-xs uppercase tracking-wide text-hm-muted">{t("total")}{size ? ` · ${size.label}` : ""}</p>
            <p className="font-display text-2xl text-hm-ink" data-testid="product-live-total">{chf(total)}</p>
            {extrasSum + optionsSum > 0 ? (
              <p className="font-sans text-[11px] text-hm-olive">+ {chf(extrasSum + optionsSum)} {t("options").toLowerCase()} / {t("each")}</p>
            ) : null}
          </div>
          <button
            type="button"
            data-testid="add-to-cart-button"
            onClick={submit}
            disabled={!product.available}
            className="flex h-14 flex-[1.4] items-center justify-center gap-2 rounded-full bg-hm-brand font-sans text-base font-extrabold text-white transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-40"
          >
            <ShoppingBag size={18} /> {editing ? t("save") : t("addToCart")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-6">
      <h3 className="mb-3 font-display text-xl text-hm-ink">{title}</h3>
      {children}
    </div>
  );
}

function Badge({ label, tone }) {
  const map = {
    brand: "bg-hm-brand text-white",
    warning: "bg-hm-terracotta text-white",
    error: "bg-hm-error text-white",
  };
  return <span className={`rounded-full px-2.5 py-1 font-sans text-[11px] font-extrabold tracking-wide ${map[tone]}`}>{label}</span>;
}
